import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { lessonRequestsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Clock,
  CheckCircle,
  XCircle,
  Calendar,
  User,
  MessageSquare,
  ArrowLeft,
  Loader2,
  CalendarClock,
  Filter,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const LessonRequestsPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isAdmin()) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    loadRequests();
  }, [statusFilter]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'ALL' ? { status: statusFilter } : {};
      const res = await lessonRequestsAPI.getAll(params);
      setRequests(res.data || []);
    } catch (error) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      setSubmitting(true);
      await lessonRequestsAPI.approve(requestId);
      toast.success('Request approved! Lesson updated.');
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      setSubmitting(true);
      await lessonRequestsAPI.reject(selectedRequest.id, rejectReason);
      toast.success('Request rejected');
      setRejectDialogOpen(false);
      setRejectReason('');
      setSelectedRequest(null);
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  };

  const openRejectDialog = (request) => {
    setSelectedRequest(request);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING':
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'APPROVED':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'REJECTED':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'RESCHEDULE':
        return <Badge className="bg-blue-100 text-blue-800"><CalendarClock className="w-3 h-3 mr-1" />Reschedule</Badge>;
      case 'CANCEL':
        return <Badge className="bg-slate-100 text-slate-800"><XCircle className="w-3 h-3 mr-1" />Cancel</Badge>;
      default:
        return <Badge>{type}</Badge>;
    }
  };

  const pendingCount = requests.filter(r => r.status === 'PENDING').length;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-primary" />
              Lesson Change Requests
              {statusFilter === 'PENDING' && pendingCount > 0 && (
                <Badge className="bg-amber-500">{pendingCount}</Badge>
              )}
            </h1>
            <p className="text-sm text-slate-500">Review and manage coach requests for lesson modifications</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={loadRequests} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="APPROVED">Approved</SelectItem>
              <SelectItem value="REJECTED">Rejected</SelectItem>
              <SelectItem value="ALL">All Requests</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Requests List */}
      <Card>
        <CardHeader>
          <CardTitle>
            {statusFilter === 'ALL' ? 'All Requests' : `${statusFilter.charAt(0) + statusFilter.slice(1).toLowerCase()} Requests`}
          </CardTitle>
          <CardDescription>
            {statusFilter === 'PENDING' 
              ? 'These requests are waiting for your review'
              : `Showing ${statusFilter.toLowerCase()} requests`
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium text-slate-500">No {statusFilter.toLowerCase()} requests</p>
              <p className="text-sm text-slate-400">
                {statusFilter === 'PENDING' 
                  ? 'All caught up! No requests need your attention.'
                  : 'No requests match this filter.'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((req) => (
                <div 
                  key={req.id}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    req.status === 'PENDING' 
                      ? 'border-amber-200 bg-amber-50/50' 
                      : 'border-slate-200 bg-slate-50'
                  }`}
                  data-testid={`request-${req.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {/* Header row */}
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {getTypeBadge(req.request_type)}
                        {getStatusBadge(req.status)}
                        <span className="text-sm text-slate-500">•</span>
                        <span className="text-sm text-slate-600">
                          Lesson #{req.lesson_number}
                        </span>
                      </div>
                      
                      {/* Student info */}
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <button 
                          onClick={() => navigate(`/students/${req.student_id}`)}
                          className="text-primary hover:underline font-medium"
                        >
                          {req.student_name || 'Unknown Student'}
                        </button>
                      </div>
                      
                      {/* Current vs Proposed */}
                      <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
                        <div>
                          <p className="text-slate-500 text-xs">Current</p>
                          <p className="font-medium">{req.current_date} {req.current_time && `at ${req.current_time}`}</p>
                        </div>
                        {req.request_type === 'RESCHEDULE' && (
                          <div>
                            <p className="text-slate-500 text-xs">Proposed</p>
                            <p className="font-medium text-blue-600">
                              {req.proposed_date} {req.proposed_time && `at ${req.proposed_time}`}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* Reason */}
                      <div className="bg-white p-2 rounded border mb-2">
                        <p className="text-xs text-slate-500">Reason:</p>
                        <p className="text-sm">{req.reason}</p>
                      </div>
                      
                      {/* Meta info */}
                      <div className="text-xs text-slate-400 flex items-center gap-3">
                        <span>Requested by {req.created_by_name}</span>
                        <span>•</span>
                        <span>{new Date(req.created_at).toLocaleDateString()}</span>
                        {req.reviewed_by_name && (
                          <>
                            <span>•</span>
                            <span>Reviewed by {req.reviewed_by_name}</span>
                          </>
                        )}
                      </div>
                      
                      {/* Review notes */}
                      {req.review_notes && (
                        <div className="mt-2 p-2 bg-slate-100 rounded text-sm">
                          <p className="text-xs text-slate-500">Admin notes:</p>
                          <p>{req.review_notes}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Actions (only for pending) */}
                    {req.status === 'PENDING' && (
                      <div className="flex flex-col gap-2">
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleApprove(req.id)}
                          disabled={submitting}
                          data-testid={`approve-${req.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => openRejectDialog(req)}
                          disabled={submitting}
                          data-testid={`reject-${req.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <AlertDialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject Request</AlertDialogTitle>
            <AlertDialogDescription>
              Please provide a reason for rejecting this {selectedRequest?.request_type?.toLowerCase()} request.
              The coach will be notified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Input
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g., No available slots on that date"
              data-testid="reject-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleReject}
              className="bg-red-600 hover:bg-red-700"
              disabled={submitting}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reject Request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LessonRequestsPage;
